import { IconButton, Paper, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import { useState, useContext, useCallback, useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { createGoal } from '../../api/goals';
import { CreateGoal, goalStatuses } from '../../api/types/Goal';
import { VentureId } from '../../api/ventures/types/Venture';
import { PageLoader, SnackMessage } from '../../components/common';
import GoalOverviewForm, {
  GoalOverviewFormValues,
} from '../../components/forms/goal-overview';
import PageTabs from '../../components/goals/page-tabs';
import BaseLayout from '../../components/layout/base-layout';
import { Header } from '../../components/layout/main-layout';
import { GoalProvider, useGoalContext } from '../../contexts/goal-context';
import { UserContext } from '../../contexts/user-context';
import { useQuery } from '../../hooks/useQuery';
import { Pages, getRoutePath } from '../../router/constants';
import { ProtectedRouteProps } from '../../router/type';
import { decodeQuery } from '../../utils/functions';

const useStyles = makeStyles({
  container: {
    flexGrow: 1,
    padding: 0,
  },
  content: {
    padding: 40,
  },
  actionsContainer: {
    display: 'flex',
    columnGap: '16px',
  },
});

function NewGoalPage({ user }: ProtectedRouteProps) {
  const query = useQuery();
  const classes = useStyles();
  const location = useLocation<{ prevPath?: string }>();
  const history = useHistory();
  const { ventures, isVenturesLoading } = useGoalContext();
  const { identityid } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const [isCreateGoalLoading, setCreateGoalLoading] = useState(false);
  const [prevPath] = useState(location.state?.prevPath);

  const ventureOptions = useMemo(
    () => ventures.map((fv) => ({ value: fv.id, label: fv.ventureName })),
    [ventures],
  );

  const initialVenture = useMemo(() => {
    const ventureEncoded = query.get('venture');

    if (ventureEncoded) {
      const decoded = decodeQuery(ventureEncoded) as any;

      // Check if it's an object with id and ventureName (new format)
      if (
        decoded &&
        typeof decoded === 'object' &&
        'id' in decoded &&
        'ventureName' in decoded
      ) {
        return {
          ventureId: decoded.id as VentureId,
          ventureName: decoded.ventureName as string,
        };
      }

      // Fallback: if it's just a string (old format), try to find in ventures list
      const ventureId = decoded as VentureId;
      const foundVenture = ventures.find((v) => v.id === ventureId);
      if (foundVenture) {
        return {
          ventureId: foundVenture.id,
          ventureName: foundVenture.ventureName,
        };
      }
    }

    return undefined;
  }, [query, ventures]);

  const handleCreateGoal = useCallback(
    async (newGoalData: CreateGoal) => {
      try {
        setCreateGoalLoading(true);
        const createdGoal = await createGoal(newGoalData);
        history.push(
          getRoutePath(Pages.GOAL_OVERVIEW, { goalId: createdGoal.id }),
        );
        enqueueSnackbar('The goal was successfully created.', {
          variant: 'success',
        });
      } catch (e: any) {
        setCreateGoalLoading(false);
        const messageError = e.response?.data?.message;
        enqueueSnackbar(
          'An error occurred while creating the goal. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
      }
    },
    [enqueueSnackbar, history],
  );

  const handleSubmit = useCallback(
    async (values: GoalOverviewFormValues) => {
      if (values.ventureId) {
        const newGoalData: CreateGoal = {
          ...values,
          tenantId: user.id,
          ownerId: identityid,
          status: goalStatuses[0],
          progress: 0,
        };
        await handleCreateGoal(newGoalData);
      }
    },
    [identityid, user, handleCreateGoal],
  );

  return (
    <BaseLayout user={user} fullHeight>
      {isVenturesLoading ? (
        <PageLoader />
      ) : (
        <>
          <Header
            title={'New Goal'}
            backLink={
              <Tooltip title='Back to Goals'>
                <IconButton
                  component={Link}
                  to={prevPath || Pages.GOALS}
                  data-testid='button-back-goals'>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Paper className={classes.container} elevation={1}>
            <PageTabs />
            <div className={classes.content}>
              <GoalOverviewForm
                handleSubmit={handleSubmit}
                loading={isCreateGoalLoading}
                ventureOptions={ventureOptions}
                initialData={initialVenture}
              />
            </div>
          </Paper>
        </>
      )}
    </BaseLayout>
  );
}

export default function NewGoalPageWithProvider(props: ProtectedRouteProps) {
  return (
    <GoalProvider>
      <NewGoalPage {...props} />
    </GoalProvider>
  );
}
